import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import {
  Scale, FileText, Search, Tag, Gavel, Building2, MapPin, Sun, Moon,
  Menu, X, LayoutDashboard, LogOut, ChevronDown, ArrowRight,
} from "lucide-react";
import { getCountyPref } from "@/lib/countyPref";
import { categoryTint } from "@/lib/categoryColors";
import api from "@/lib/api";
import { saveThemeToAccount, applyTheme } from "@/lib/theme";
import { useSosEnabled } from "@/lib/siteConfig";

export default function TopNav() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [county, setCounty] = useState(getCountyPref()?.county || null);
  const [dark, setDark] = useState(() => localStorage.getItem("nc_theme") !== "light");
  const [hideSos, setHideSos] = useState(false);
  const siteSosOn = useSosEnabled();
  const [scrolled, setScrolled] = useState(false);
  const [cats, setCats] = useState([]);
  const [popular, setPopular] = useState([]);

  useEffect(() => {
    api.get("/forms/categories")
      .then(({ data }) => setCats((data.categories || []).filter((c) => !(c.name || "").startsWith("SOS ·")).slice(0, 8)))
      .catch(() => {});
    api.get("/forms/popular").then(({ data }) => setPopular(data.forms || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { if (user) api.get("/profile").then(({ data }) => setHideSos(!!data?.hide_sos)).catch(() => {}); }, [user]);
  useEffect(() => {
    const sync = () => setCounty(getCountyPref()?.county || null);
    window.addEventListener("nc-county-pref-changed", sync);
    return () => window.removeEventListener("nc-county-pref-changed", sync);
  }, []);
  useEffect(() => {
    const sync = (e) => setHideSos(!!(e.detail ?? false));
    window.addEventListener("nc-sos-pref-changed", sync);
    return () => window.removeEventListener("nc-sos-pref-changed", sync);
  }, []);
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("nc_theme", dark ? "dark" : "light");
  }, [dark]);
  useEffect(() => {
    const sync = () => setDark(localStorage.getItem("nc_theme") !== "light");
    window.addEventListener("nc-theme-changed", sync);
    return () => window.removeEventListener("nc-theme-changed", sync);
  }, []);
  const toggleTheme = () => { const next = !dark; setDark(next); const p = next ? "dark" : "light"; applyTheme(p); if (user) saveThemeToAccount(p); };
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: "/forms", label: t("nav.courtForms"), icon: FileText, testid: "nav-forms" },
    ...(!hideSos && siteSosOn ? [{ to: "/sos-forms", label: t("nav.sosForms"), icon: Building2, testid: "nav-sos-forms" }] : []),
    { to: "/find", label: t("nav.finder"), icon: Search, testid: "nav-finder" },
    { to: "/find-attorney", label: t("nav.findAttorney"), icon: Gavel, testid: "nav-find-attorney" },
    { to: "/pricing", label: t("nav.pricing"), icon: Tag, testid: "nav-pricing" },
    { to: "/for-attorneys", label: t("nav.forAttorneys"), icon: Scale, testid: "nav-for-attorneys" },
  ];

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");
  const handleLogout = async () => { await logout(); navigate("/"); };

  return (
    <header data-testid="top-nav" className={`no-print sticky top-0 z-50 border-b bg-background/85 backdrop-blur-md transition-shadow duration-300 ${scrolled ? "shadow-md border-transparent" : "border-border"}`}>
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-4 transition-[height] duration-300 ${scrolled ? "h-12" : "h-16"}`}>
        {/* Brand */}
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
          <span className={`grid place-items-center bg-primary text-primary-foreground transition-all duration-300 ${scrolled ? "w-8 h-8" : "w-9 h-9"}`}><Scale size={scrolled ? 16 : 18} /></span>
          <span className="font-serif text-lg font-semibold tracking-tight leading-none">
            NC Legal Forms
            <span className={`block overline text-[0.5rem] text-accent tracking-[0.22em] overflow-hidden transition-all duration-300 ${scrolled ? "max-h-0 opacity-0" : "max-h-4 opacity-100"}`}>STATE OF NORTH CAROLINA</span>
          </span>
        </Link>

        {/* Desktop links */}
        <nav className="hidden lg:flex items-center gap-1 text-sm font-medium">
          {links.map((l) => l.to === "/forms" ? (
            <div key={l.to} className="relative group/mega">
              <Link to={l.to} data-testid={l.testid}
                className={`relative flex items-center gap-1 px-3 py-2 rounded-sm whitespace-nowrap transition-colors ${isActive(l.to) ? "text-accent font-semibold" : "text-muted-foreground hover:text-foreground"}`}>
                {l.label}
                <ChevronDown size={13} className="transition-transform duration-200 group-hover/mega:rotate-180" />
                <span className={`pointer-events-none absolute left-3 right-6 -bottom-[7px] h-0.5 bg-accent origin-left transition-transform duration-300 ease-out ${isActive(l.to) ? "scale-x-100" : "scale-x-0 group-hover/mega:scale-x-100"}`} />
              </Link>
              <div className="absolute left-0 top-full pt-3 opacity-0 invisible translate-y-1 group-hover/mega:opacity-100 group-hover/mega:visible group-hover/mega:translate-y-0 transition-all duration-200 z-50">
                <div data-testid="mega-menu" className="w-[720px] bg-background border-2 border-border shadow-xl p-4 flex gap-4">
                  <div className="flex-1">
                    <p className="overline text-accent text-[0.6rem] mb-3 px-1">{t("nav.byCategory")}</p>
                    <div className="grid grid-cols-2 gap-1">
                      {cats.map((c) => (
                        <Link key={c.name} to={`/forms?category=${encodeURIComponent(c.name)}`} data-testid={`mega-cat-${c.name}`}
                          className="flex items-center justify-between gap-2 px-3 py-2 rounded-sm hover:bg-secondary transition-colors">
                          <span className="flex items-center gap-2.5 min-w-0">
                            <span className="w-2.5 h-2.5 shrink-0 border border-black/20" style={{ backgroundColor: categoryTint(c.name) }} />
                            <span className="truncate">{c.name}</span>
                          </span>
                          <span className="text-xs text-muted-foreground shrink-0">{c.count}</span>
                        </Link>
                      ))}
                    </div>
                    <Link to="/forms" data-testid="mega-browse-all"
                      className="mt-2 pt-3 border-t border-border flex items-center gap-1.5 px-1 text-sm font-semibold text-accent hover:gap-2.5 transition-all">
                      {t("nav.browseAll")} <ArrowRight size={15} />
                    </Link>
                  </div>
                  <div className="w-56 shrink-0 border-l border-border pl-4" data-testid="mega-popular">
                    <p className="overline text-accent text-[0.6rem] mb-3 px-1">{t("nav.mostUsed")}</p>
                    <div className="space-y-0.5">
                      {popular.map((f) => (
                        <Link key={f.number} to={`/forms/${f.number}`} data-testid={`mega-popular-${f.number}`}
                          className="flex items-start gap-2.5 px-3 py-2 rounded-sm hover:bg-secondary transition-colors group/pf">
                          <FileText size={15} className="text-accent shrink-0 mt-0.5" />
                          <span className="min-w-0">
                            <span className="block text-sm leading-tight truncate group-hover/pf:text-accent transition-colors">{f.title}</span>
                            <span className="block text-[0.7rem] text-muted-foreground">{f.number}</span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Link key={l.to} to={l.to} data-testid={l.testid}
              className={`group relative px-3 py-2 rounded-sm whitespace-nowrap transition-colors ${isActive(l.to) ? "text-accent font-semibold" : "text-muted-foreground hover:text-foreground"}`}>
              {l.label}
              <span className={`pointer-events-none absolute left-3 right-3 -bottom-[7px] h-0.5 bg-accent origin-left transition-transform duration-300 ease-out ${isActive(l.to) ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
            </Link>
          ))}
        </nav>

        {/* Right controls */}
        <div className="hidden lg:flex items-center gap-2 shrink-0">
          <button
            data-testid="nav-county-chip"
            onClick={() => window.dispatchEvent(new Event("open-county-gate"))}
            title={county ? t("nav.countyName", { name: county }) : t("nav.setCounty")}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border-2 border-border text-xs font-semibold hover:border-accent hover:text-accent transition-colors">
            <MapPin size={14} /> {county ? t("nav.countyName", { name: county }) : t("nav.setCounty")}
          </button>
          <LanguageSwitcher />
          <button onClick={toggleTheme} data-testid="theme-toggle-btn" aria-label="Toggle theme"
            className="grid place-items-center w-9 h-9 rounded-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
            {dark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          {user ? (
            <>
              <Link to="/dashboard"><Button size="sm" variant="outline" data-testid="nav-dashboard" className="rounded-sm gap-2"><LayoutDashboard size={15} /> {t("nav.dashboard")}</Button></Link>
              <Button onClick={handleLogout} size="sm" variant="ghost" data-testid="nav-logout-btn" className="rounded-sm gap-2 text-muted-foreground"><LogOut size={15} /> {t("nav.signout")}</Button>
            </>
          ) : (
            <>
              <Link to="/login"><Button size="sm" variant="outline" data-testid="nav-login-btn" className="rounded-sm">{t("nav.login")}</Button></Link>
              <Link to="/pricing"><Button size="sm" data-testid="nav-cta-btn" className="rounded-sm bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground transition-colors">{t("nav.getAccess")}</Button></Link>
            </>
          )}
        </div>

        {/* Mobile toggle */}
        <button data-testid="nav-mobile-toggle" onClick={() => setOpen((o) => !o)}
          className="lg:hidden grid place-items-center w-10 h-10 border-2 border-border text-foreground hover:bg-secondary transition-colors">
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* Mobile panel */}
      {open && (
        <div data-testid="nav-mobile-panel" className="lg:hidden border-t border-border bg-background px-4 py-4 space-y-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`m-${l.testid}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${isActive(l.to) ? "bg-secondary text-accent font-semibold" : "text-foreground hover:bg-secondary"}`}>
              <l.icon size={17} /> {l.label}
            </Link>
          ))}
          <div className="flex items-center justify-between pt-3 mt-2 border-t border-border">
            <button
              onClick={() => window.dispatchEvent(new Event("open-county-gate"))}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border-2 border-border text-xs font-semibold">
              <MapPin size={14} /> {county ? t("nav.countyName", { name: county }) : t("nav.setCounty")}
            </button>
            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <button onClick={toggleTheme} aria-label="Toggle theme"
                className="grid place-items-center w-9 h-9 rounded-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
                {dark ? <Sun size={16} /> : <Moon size={16} />}
              </button>
            </div>
          </div>
          <div className="pt-2 grid grid-cols-2 gap-2">
            {user ? (
              <>
                <Link to="/dashboard" className="block"><Button size="sm" variant="outline" className="w-full rounded-sm gap-2"><LayoutDashboard size={15} /> {t("nav.dashboard")}</Button></Link>
                <Button onClick={handleLogout} size="sm" variant="outline" data-testid="m-nav-logout-btn" className="w-full rounded-sm gap-2"><LogOut size={15} /> {t("nav.signout")}</Button>
              </>
            ) : (
              <>
                <Link to="/login" className="block"><Button size="sm" variant="outline" data-testid="m-nav-login-btn" className="w-full rounded-sm">{t("nav.login")}</Button></Link>
                <Link to="/pricing" className="block"><Button size="sm" data-testid="m-nav-cta-btn" className="w-full rounded-sm bg-accent text-accent-foreground">{t("nav.getAccess")}</Button></Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
